import React, { useState } from "react";
import { PhoneLink } from "@/utils/PhoneLink";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Drawer, Divider, IconButton, Tooltip, Snackbar,
  FormControlLabel, Checkbox, Stack, Autocomplete, MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { useLocation } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import WriteOffButton from "@/components/zephyr/WriteOffButton";
import EditableInvAmount from "@/components/zephyr/EditableInvAmount";
import ZephyrEmailButtons from "@/components/zephyr/ZephyrEmailButtons";
import ZephyrComposeModal from "@/components/zephyr/ZephyrComposeModal";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

function scriptStatusChip(s) {
  if (s.PaymentID) return <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.72rem" }} />;
  if (Number(s.WrittenOff)) return <Chip label="Written Off" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.72rem" }} />;
  if (Number(s.OnHold) === -1) return <Chip label="Missed" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.72rem" }} />;
  return <Chip label="Not Paid" size="small" variant="outlined" sx={{ fontSize: "0.72rem" }} />;
}

function scriptRowBg(s) {
  if (s.PaymentID) return "#f0fdf4";
  if (Number(s.WrittenOff)) return "#fef2f2";
  if (Number(s.OnHold) === -1) return "#fefce8";
  return "transparent";
}

// ── Full Script Report Drawer ─────────────────────────────────────
function FullReportDrawer({ open, claimID, customerName, onClose, onOpenPayment, onOpenRecon, outstandingOnly }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !claimID) return;
    setLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_FULL_SCRIPT_REPORT}/${claimID}`, "GET")
      .then((res) => setScripts(res?.data?.scripts ?? []))
      .finally(() => setLoading(false));
  }, [open, claimID]);

  const displayed = outstandingOnly
    ? scripts.filter((s) => !s.PaymentID && !Number(s.WrittenOff))
    : scripts;
  const drawerTitle = outstandingOnly ? "Outstanding Scripts" : "Full Script Report";

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", md: 900 }, display: "flex", flexDirection: "column" } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5,
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff", flexShrink: 0 }}>
        <Box>
          <Typography fontWeight={700}>{drawerTitle}</Typography>
          {customerName && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>{customerName}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Batch Code", "Invoice", "Date", "Item", "Qty", "Invoiced", "Received", "Status", "Payment", "Statement"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((s) => (
                  <TableRow key={s.ScriptID} sx={{ backgroundColor: scriptRowBg(s), "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                    <TableCell>{s.ScrBatchID ?? "—"}</TableCell>
                    <TableCell>{s.InvoiceID ?? "—"}</TableCell>
                    <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell>{s.Qty ?? "—"}</TableCell>
                    <TableCell align="right">{s.InvAmount != null ? fmt(s.InvAmount) : "—"}</TableCell>
                    <TableCell align="right">{s.RcvdAmount != null ? fmt(s.RcvdAmount) : "—"}</TableCell>
                    <TableCell>{scriptStatusChip(s)}</TableCell>
                    <TableCell>
                      {s.PaymentID ? (
                        <Tooltip title="Open payment">
                          <Chip label={`#${s.PaymentID}`} size="small" onClick={() => onOpenPayment(s.PaymentID)}
                            sx={{ cursor: "pointer", fontSize: "0.72rem" }} />
                        </Tooltip>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {s.ReconcilID ? (
                        <Tooltip title="Open statement">
                          <Chip label={`Stmt #${s.ReconcilID}`} size="small" onClick={() => onOpenRecon(s.ReconcilID)}
                            sx={{ cursor: "pointer", fontSize: "0.72rem", background: `${Z_PURPLE}18`, color: Z_PURPLE }} />
                        </Tooltip>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Drawer>
  );
}

// ── Payment Modal ─────────────────────────────────────────────────
function PaymentModal({ open, paymentID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !paymentID) return;
    setLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_PAYMENT_DETAILS}/${paymentID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, paymentID]);

  const h = data?.header;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Payment #{paymentID}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? <CircularProgress sx={{ color: Z_PURPLE }} /> : (
          <>
            {h && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2 }}>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Pay Date</Typography>
                  <Typography variant="body2">{fmtDate(h.PayDate)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Total Value</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmt(h.TotalPaymentValue)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Reference</Typography>
                  <Typography variant="body2">{h.ReferenceNumber || "—"}</Typography></Box>
                {h.PaymentComment && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                    <Typography variant="body2">{h.PaymentComment}</Typography>
                  </Box>
                )}
              </Box>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Patient", "Invoice", "Item", "Invoiced", "Received"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.FullName}</TableCell>
                      <TableCell>{s.InvoiceID}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                      <TableCell align="right">{fmt(s.RcvdAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Reconciliation Modal ──────────────────────────────────────────
function ReconModal({ open, reconID, onClose }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !reconID) return;
    setLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_RECONCILIATION_DETAILS}/${reconID}`, "GET")
      .then((res) => setScripts(res?.data?.scripts ?? []))
      .finally(() => setLoading(false));
  }, [open, reconID]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Statement #{reconID}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? <CircularProgress sx={{ color: Z_PURPLE }} /> : (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Patient", "Invoice", "Item", "Invoiced", "Received", "Date"].map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scripts.map((s) => (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                    <TableCell>{s.FullName}</TableCell>
                    <TableCell>{s.InvoiceID}</TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                    <TableCell align="right">{fmt(s.RcvdAmount)}</TableCell>
                    <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Customer Note Edit Modal ──────────────────────────────────────
const NOTE_MAX = 2000;

function CustomerNoteModal({ open, initialValue, customerID, customerName, onClose, onSaved }) {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (open) setValue(initialValue ?? ""); }, [open, initialValue]);

  const original = initialValue ?? "";
  const dirty = value !== original;

  const save = async () => {
    if (!dirty) { onClose(); return; }
    setSaving(true);
    await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_UPDATE_CUSTOMER_COMMENT}/${customerID}`,
      "PUT",
      { comment: value }
    ).catch(() => {});
    setSaving(false);
    onSaved(value);
    onClose();
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); save(); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", boxShadow: "0 24px 64px rgba(56,24,79,0.22)" } }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff", px: 2.5, py: 1.75,
        display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, backgroundColor: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <StickyNote2OutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize="1.02rem" lineHeight={1.2}>Customer Note</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ pt: "20px !important", pb: 1.5 }}>
        <TextField
          multiline minRows={6} maxRows={14} fullWidth autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, NOTE_MAX))}
          onKeyDown={handleKeyDown}
          placeholder="Add context about this customer"
          inputProps={{ maxLength: NOTE_MAX }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#faf9fc", fontSize: "0.9rem", lineHeight: 1.55,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: `${Z_PURPLE}66` },
              "&.Mui-focused fieldset": { borderColor: Z_PURPLE, borderWidth: 2 } },
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
          </Typography>
          <Typography variant="caption" sx={{ color: value.length >= NOTE_MAX ? "#dc2626" : "text.disabled", fontWeight: 600 }}>
            {value.length.toLocaleString()} / {NOTE_MAX.toLocaleString()}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.25, pt: 0.5, gap: 0.5 }}>
        {value.trim() && (
          <Button onClick={() => setValue("")} disabled={saving}
            sx={{ textTransform: "none", color: "#64748b", mr: "auto" }}>
            Clear
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}
          sx={{ textTransform: "none", color: "#475569" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, boxShadow: "none",
            "&:hover": { background: `linear-gradient(135deg, #2a0f3d, ${Z_PURPLE})` },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" } }}>
          {saving ? "Saving…" : "Save Note"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit Claim Modal ──────────────────────────────────────────────
// Edits the CLAIM behind the searched customer: insurer, date of injury,
// employer (optional) and who the invoice is addressed to.
const toDateInputValue = (d) => (d ? String(d).slice(0, 10) : "");

function EditClaimModal({ open, claim, onClose, onSaved }) {
  const [insurers, setInsurers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [claimNumber, setClaimNumber] = useState("");
  const [insCompID, setInsCompID] = useState(null);
  const [employerID, setEmployerID] = useState(null);
  const [dateOfInjury, setDateOfInjury] = useState("");
  const [invoiceTo, setInvoiceTo] = useState("");

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setClaimNumber(claim?.ClaimNumber ?? "");
    setInsCompID(claim?.InsCompID ?? null);
    setEmployerID(claim?.EmployerID ?? null);
    setDateOfInjury(toDateInputValue(claim?.DateOfInjury));
    setInvoiceTo(claim?.InvoiceTo ?? "");
    setLoadingLookups(true);
    Promise.all([
      phappsRequest(API_PATHS.ZEPHYR_ADMIN_INSURERS, "GET").catch(() => null),
      phappsRequest(API_PATHS.ZEPHYR_ADMIN_EMPLOYERS, "GET").catch(() => null),
      phappsRequest(API_PATHS.ZEPHYR_ADMIN_INVOICE_RECIPIENTS, "GET").catch(() => null),
    ]).then(([ins, emp, rec]) => {
      setInsurers(ins?.data ?? []);
      setEmployers(emp?.data ?? []);
      setRecipients(rec?.data ?? []);
    }).finally(() => setLoadingLookups(false));
  }, [open, claim]);

  const selectedInsurer = insurers.find((i) => i.InsCompID === insCompID) ?? null;
  const selectedEmployer = employers.find((e) => e.EmployerID === employerID) ?? null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_UPDATE_CLAIM}/${claim.ClaimID}`, "PUT", {
        ClaimNumber: claimNumber.trim(),
        InsCompID: insCompID ?? null,
        EmployerID: employerID ?? null,
        DateOfInjury: dateOfInjury || null,
        ...(invoiceTo ? { InvoiceTo: Number(invoiceTo) } : {}),
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to save claim.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <span>Edit Claim{claim?.FullName ? ` — ${claim.FullName}` : ""}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "24px !important", display: "flex", flexDirection: "column", gap: 2.25 }}>
        {loadingLookups ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : (
          <>
            <TextField
              label="Claim number" size="small" fullWidth
              value={claimNumber}
              onChange={(e) => setClaimNumber(e.target.value)}
            />
            <Autocomplete
              options={insurers}
              getOptionLabel={(o) => o?.InsCompName ?? ""}
              isOptionEqualToValue={(o, v) => o.InsCompID === v?.InsCompID}
              value={selectedInsurer}
              onChange={(_, v) => setInsCompID(v ? v.InsCompID : null)}
              renderInput={(params) => <TextField {...params} label="Insurance company" size="small" />}
            />
            <TextField
              type="date" label="Date of injury" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateOfInjury}
              onChange={(e) => setDateOfInjury(e.target.value)}
            />
            <Autocomplete
              options={employers}
              getOptionLabel={(o) => o?.EmployerName ?? ""}
              isOptionEqualToValue={(o, v) => o.EmployerID === v?.EmployerID}
              value={selectedEmployer}
              onChange={(_, v) => setEmployerID(v ? v.EmployerID : null)}
              renderInput={(params) => <TextField {...params} label="Employer (optional)" size="small" />}
            />
            <TextField
              select label="Invoice to" size="small" fullWidth
              value={invoiceTo}
              onChange={(e) => setInvoiceTo(e.target.value)}
            >
              {recipients.map((r) => (
                <MenuItem key={r.InvRecID} value={r.InvRecID}>{r.ReceiverName}</MenuItem>
              ))}
            </TextField>
            {error && <Alert severity="error">{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || loadingLookups}
          sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
          {saving ? "Saving…" : "Save Claim"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────
function InvoiceDetailModal({ open, invoiceID, claimID, onClose, onWriteOff }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  React.useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoiceID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, invoiceID, reloadKey]);

  const [priceError, setPriceError] = useState(null);
  const reloadModal = () => { setReloadKey((k) => k + 1); onWriteOff?.(); };

  const c = data?.claim;
  const scripts = data?.scripts ?? [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>Invoice #{invoiceID}</span>
          {c && (
            <WriteOffButton
              claimID={c.ClaimID ?? claimID}
              invoiceID={invoiceID}
              scripts={scripts}
              onSuccess={reloadModal}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : (
          <>
            {c && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Patient</Typography>
                  <Typography variant="body2" fontWeight={600}>{c.FullName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Chemist</Typography>
                  <Typography variant="body2">{c.ChemistName || c.ChemShortCode || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Date</Typography>
                  <Typography variant="body2">{fmtDate(c.InvoiceDate)}</Typography>
                </Box>
                {c.InvComment && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                    <Typography variant="body2">{c.InvComment}</Typography>
                  </Box>
                )}
              </Box>
            )}
            {priceError && (
              <Alert severity="warning" onClose={() => setPriceError(null)} sx={{ mb: 1.5 }}>
                {priceError}
              </Alert>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Item", "Dispensed", "Qty", "Invoiced", "Status", ""].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => (
                    <TableRow key={s.ScriptID} sx={{ backgroundColor: scriptRowBg(s), "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell>{s.Qty ?? "—"}</TableCell>
                      <TableCell align="right">
                        <EditableInvAmount
                          script={s}
                          onSaved={() => { setPriceError(null); reloadModal(); }}
                          onError={setPriceError}
                        />
                      </TableCell>
                      <TableCell>{scriptStatusChip(s)}</TableCell>
                      <TableCell sx={{ px: "4px !important" }}>
                        <WriteOffButton
                          claimID={c?.ClaimID ?? claimID}
                          scriptIDs={[s.ScriptID]}
                          scripts={[s]}
                          onSuccess={reloadModal}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Invoice Comment Modal ─────────────────────────────────────────
const toDateInput = (d) => d.toISOString().slice(0, 10);
const addDaysToToday = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return toDateInput(d); };

function InvoiceCommentModal({ open, invoiceID, claimID, currentComment, onClose, onSaved }) {
  const [value, setValue] = useState(currentComment ?? "");
  const [saving, setSaving] = useState(false);
  const [recheckDate, setRecheckDate] = useState(() => addDaysToToday(7));
  const [xDays, setXDays] = useState(14);

  React.useEffect(() => {
    if (open) {
      setValue(currentComment ?? "");
      setRecheckDate(addDaysToToday(7));
      setXDays(14);
    }
  }, [open, currentComment]);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_UPDATE_INVOICE_COMMENT}/${invoiceID}`,
      "POST",
      { comment: value.trim(), recheckDate }
    ).catch(() => {});
    const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_ADD_CLAIM_COMMENT}/${claimID}`, "POST", {
      comment: `${value.trim()} (Invoice #${invoiceID})`,
    }).catch(() => null);
    setSaving(false);
    onSaved({ invoiceID, comment: value.trim(), recheckDate, newComments: res?.data ?? null });
    onClose();
  };

  const chipBtn = (label, onClick) => (
    <Button key={label} size="small" variant="outlined" onClick={onClick}
      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.3, px: 1, minWidth: 0, borderColor: "#cbd5e1", color: "#475569" }}>
      {label}
    </Button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <span>Invoice #{invoiceID} — Comment</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "24px !important", display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField multiline rows={3} fullWidth size="small" autoFocus
          placeholder="Enter invoice comment…"
          value={value} onChange={(e) => setValue(e.target.value)} />
        <Typography variant="caption" color="text.secondary">
          Also logged to claim comments as: &ldquo;{value.trim() || "…"} (Invoice #{invoiceID})&rdquo;
        </Typography>

        {/* Recheck date */}
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Recheck Date
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
            {chipBtn("1 Week", () => setRecheckDate(addDaysToToday(7)))}
            {chipBtn("1 Month", () => setRecheckDate(addDaysToToday(30)))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <input
                type="number" min={1} max={365} value={xDays}
                onChange={(e) => setXDays(Math.max(1, Number(e.target.value)))}
                style={{ width: 52, padding: "3px 6px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: "0.8rem" }}
              />
              {chipBtn("Days", () => setRecheckDate(addDaysToToday(xDays)))}
            </Box>
          </Box>
          <TextField
            type="date" size="small" fullWidth
            value={recheckDate}
            onChange={(e) => setRecheckDate(e.target.value)}
            inputProps={{ min: toDateInput(new Date()) }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !value.trim()}
          sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
          {saving ? "Saving…" : "Save & Log"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Merge Invoices Modal ──────────────────────────────────────────
function MergeInvoicesModal({ open, outstandingInvoices, claimID, onClose, onSuccess, onError }) {
  const [selected, setSelected] = React.useState(new Set());
  const [confirming, setConfirming] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelected(new Set(outstandingInvoices.map(inv => inv.InvoiceID)));
      setConfirming(false);
    }
  }, [open, outstandingInvoices]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectedInvoices = outstandingInvoices.filter(inv => selected.has(inv.InvoiceID));
  const totalValue = selectedInvoices.reduce((s, inv) => s + Number(inv.InvoiceValue ?? 0), 0);
  const canProceed = selected.size >= 2;

  const handleMerge = async () => {
    setLoading(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_MERGE_INVOICES, "POST", {
        claimID,
        invoiceIDs: [...selected],
      });
      onSuccess?.("Invoices merged successfully");
      onClose();
    } catch (err) {
      onError?.(err?.message || "Merge failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>{confirming ? "Confirm Merge" : "Merge Invoices"}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {!confirming ? (
        <>
          <DialogContent sx={{ pt: "20px !important" }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Merging will create a <strong>new invoice</strong> and move all{" "}
              <strong>unpaid, non-written-off</strong> scripts from the selected invoices into it.
              Paid and written-off scripts stay on their original invoices. This cannot be undone.
            </Alert>
            {outstandingInvoices.length < 2 ? (
              <Typography variant="body2" color="text.secondary">
                At least 2 outstanding invoices are required to merge.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {outstandingInvoices.map(inv => (
                  <FormControlLabel
                    key={inv.InvoiceID}
                    control={
                      <Checkbox
                        checked={selected.has(inv.InvoiceID)}
                        onChange={() => toggle(inv.InvoiceID)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>
                          Invoice #{inv.InvoiceID}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#dc2626" }}>
                          {fmt(inv.InvoiceValue)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(inv.InvoiceDate)} · {inv.ScriptCount} script{inv.ScriptCount !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button
              onClick={() => setConfirming(true)}
              variant="contained"
              color="warning"
              disabled={!canProceed}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Review Merge ({selected.size} invoice{selected.size !== 1 ? "s" : ""})
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent sx={{ pt: "20px !important" }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              The following invoices will be merged into a single new invoice. This cannot be undone.
            </Alert>
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Invoice", "Date", "Scripts", "Value"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 0.75 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedInvoices.map(inv => (
                    <TableRow key={inv.InvoiceID} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                      <TableCell sx={{ fontWeight: 700, color: Z_PURPLE }}>#{inv.InvoiceID}</TableCell>
                      <TableCell>{fmtDate(inv.InvoiceDate)}</TableCell>
                      <TableCell align="center">{inv.ScriptCount}</TableCell>
                      <TableCell align="right" sx={{ color: "#dc2626" }}>{fmt(inv.InvoiceValue)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ "& td": { py: 0.5, fontWeight: 700, borderTop: "2px solid #e2e8f0" } }}>
                    <TableCell colSpan={3}>Total</TableCell>
                    <TableCell align="right" sx={{ color: "#dc2626" }}>{fmt(totalValue)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary">
              A comment will be automatically logged recording which invoices were merged.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)} sx={{ textTransform: "none" }} disabled={loading}>
              Back
            </Button>
            <Button
              onClick={handleMerge}
              variant="contained"
              color="error"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {loading ? "Merging…" : "Confirm Merge"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function ZephyrCustomersPage() {
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [claimID, setClaimID] = useState(null);
  const [data, setData] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [editingCommentID, setEditingCommentID] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [deletingCommentID, setDeletingCommentID] = useState(null);
  const [deleteConfirmComment, setDeleteConfirmComment] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editClaimOpen, setEditClaimOpen] = useState(false);
  const [reportMode, setReportMode] = useState(null); // 'full' | 'outstanding'
  const [paymentModal, setPaymentModal] = useState(null);
  const [reconModal, setReconModal] = useState(null);
  const [invoiceDetailID, setInvoiceDetailID] = useState(null);
  const [invoiceCommentState, setInvoiceCommentState] = useState(null);
  const [toast, setToast] = useState(null); // { msg, severity }
  const [composeModal, setComposeModal] = useState(null); // { invoiceID?, defaultTo? }
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  const showToast = (msg, severity = "success") => setToast({ msg, severity });
  const hideToast = () => setToast(null);

  // Auto-load patient when navigated here from another page (e.g. Payments) with a claimID
  React.useEffect(() => {
    const navClaimID = location.state?.claimID;
    if (!navClaimID) return;
    setClaimID(navClaimID);
    setResults(null);
    setData(null);
    setCommentText("");
    setPatientLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CUSTOMER_DETAILS}/${navClaimID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setPatientLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomer = (id) => {
    setPatientLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CUSTOMER_DETAILS}/${id}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setPatientLoading(false));
  };

  const doSearch = () => {
    if (!search.trim()) return;
    setData(null);
    setClaimID(null);
    setSearchLoading(true);
    setSearchError(null);
    setResults(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CUSTOMER_SEARCH}?search=${encodeURIComponent(search.trim())}`, "GET")
      .then((res) => setResults(res?.data ?? []))
      .catch(() => setSearchError("Search failed."))
      .finally(() => setSearchLoading(false));
  };

  const selectPatient = (r) => {
    setClaimID(r.ClaimID);
    setResults(null);
    setData(null);
    setCommentText("");
    setPatientLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CUSTOMER_DETAILS}/${r.ClaimID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setPatientLoading(false));
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    setAddingComment(true);
    const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_ADD_CLAIM_COMMENT}/${claimID}`, "POST", {
      comment: commentText.trim(),
    }).catch(() => null);
    if (res?.data) setData((prev) => prev ? { ...prev, comments: res.data } : prev);
    setCommentText("");
    setAddingComment(false);
  };

  const startEditComment = (cm) => {
    setEditingCommentID(cm.CommentID);
    setEditingCommentText(cm.Comment);
  };

  const cancelEditComment = () => {
    setEditingCommentID(null);
    setEditingCommentText("");
  };

  const saveEditComment = async () => {
    if (!editingCommentText.trim() || !editingCommentID) return;
    setSavingComment(true);
    const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CLAIM_COMMENT}/${editingCommentID}`, "PUT", {
      comment: editingCommentText.trim(),
    }).catch(() => null);
    setSavingComment(false);
    if (res?.data) {
      setData((prev) => prev ? { ...prev, comments: res.data } : prev);
      cancelEditComment();
      showToast("Comment updated.");
    } else {
      showToast("Failed to update comment.", "error");
    }
  };

  const confirmDeleteComment = (cm) => setDeleteConfirmComment(cm);

  const deleteComment = async () => {
    const cm = deleteConfirmComment;
    if (!cm) return;
    setDeletingCommentID(cm.CommentID);
    const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CLAIM_COMMENT}/${cm.CommentID}`, "DELETE").catch(() => null);
    setDeletingCommentID(null);
    setDeleteConfirmComment(null);
    if (res?.data) {
      setData((prev) => prev ? { ...prev, comments: res.data } : prev);
      showToast("Comment deleted.");
    } else {
      showToast("Failed to delete comment.", "error");
    }
  };

  const handleInvoiceCommentSaved = ({ invoiceID, comment, recheckDate, newComments }) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        outstandingInvoices: prev.outstandingInvoices.map((inv) =>
          inv.InvoiceID === invoiceID ? { ...inv, InvComment: comment, RecheckDate: recheckDate } : inv
        ),
        comments: newComments ?? prev.comments,
      };
    });
  };

  const cl = data?.claim;
  const comments = data?.comments ?? [];
  const outstandingInvoices = data?.outstandingInvoices ?? [];

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Customers</Typography>
      </Box>

      {/* ── Search bar (always visible) ── */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          label="Last name  (or  last name  first name)"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doSearch()}
          sx={{ width: 300 }}
          autoFocus
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={doSearch}
          disabled={searchLoading}
          sx={{
            textTransform: "none", fontWeight: 700,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
            "&:hover": { background: `linear-gradient(135deg, #1a0a2e, ${Z_PURPLE})` },
          }}
        >
          Search
        </Button>
        {cl && (
          <>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditClaimOpen(true)}
              sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}
            >
              Edit Claim
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              onClick={() => setReportMode("full")}
              sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}
            >
              Full Script Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReceiptLongOutlinedIcon />}
              onClick={() => setReportMode("outstanding")}
              sx={{ textTransform: "none", borderColor: Z_BLUE, color: Z_BLUE }}
            >
              Outstanding Scripts
            </Button>
          </>
        )}
      </Box>

      {searchLoading && <CircularProgress size={28} sx={{ color: Z_PURPLE }} />}
      {searchError && <Alert severity="error">{searchError}</Alert>}

      {/* ── Search results ── */}
      {results !== null && (
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
              <TableRow>
                {["Name", "Claim Number", "Address", "Chemist"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No results.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {results.map((r) => (
                <TableRow key={r.ClaimID} hover onClick={() => selectPatient(r)}
                  sx={{ cursor: "pointer", "& td": { py: 0.75, px: 1.5, fontSize: "0.82rem" } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.FullName}</TableCell>
                  <TableCell>{r.ClaimNumber}</TableCell>
                  <TableCell>{[r.CustomerAddress, r.CustomerSuburb].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell>{r.ChemistName || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Patient loading ── */}
      {patientLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
          <CircularProgress sx={{ color: Z_PURPLE }} />
        </Box>
      )}

      {/* ── Patient detail (inline) ── */}
      {cl && (
        <>
          {/* Top card: 3fr patient | 1fr owing | 1fr invoiced-to */}
          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr", gap: 1.5, alignItems: "stretch" }}>

            {/* Patient data */}
            <Box sx={{
              background: `linear-gradient(135deg, ${Z_PURPLE}10, ${Z_BLUE}08)`,
              border: `1px solid ${Z_PURPLE}22`,
              borderRadius: 2,
              p: 1.5,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1,
              alignContent: "start",
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Full Name</Typography>
                <Typography variant="body2" fontWeight={700}>{cl.FullName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Claim Number</Typography>
                <Typography variant="body2">{cl.ClaimNumber || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Date of Birth</Typography>
                <Typography variant="body2">{cl.CustomerDOB ? fmtDate(cl.CustomerDOB) : "—"}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Phone</Typography>
                <Typography variant="body2">{cl.CustomerPhone || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Date of Injury</Typography>
                <Typography variant="body2">{cl.DateOfInjury ? fmtDate(cl.DateOfInjury) : "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Address</Typography>
                <Typography variant="body2">
                  {[cl.CustomerAddress, cl.CustomerSuburb, cl.CustomerPostcode].filter(Boolean).join(", ") || "—"}
                </Typography>
              </Box>

              {/* Chemist row — only renders if present */}
              {cl.ChemistName && (
                <Box sx={{ gridColumn: "1 / -1", pt: 0.75, borderTop: "1px solid #e2e8f0",
                  display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Chemist</Typography>
                    <Typography variant="body2" fontWeight={600}>{cl.ChemistName}</Typography>
                  </Box>
                  {cl.ChemistPhone && (
                    <Typography variant="body2" color="text.secondary">
                      <PhoneLink phone={cl.ChemistPhone} />
                    </Typography>
                  )}
                  {cl.ChemistEmail && (
                    <Button size="small" startIcon={<EmailOutlinedIcon fontSize="small" />}
                      href={`mailto:${cl.ChemistEmail}`}
                      sx={{ textTransform: "none", color: Z_BLUE, p: 0, minWidth: 0, fontSize: "0.75rem" }}>
                      Email chemist
                    </Button>
                  )}
                </Box>
              )}

              {/* Customer Notes — full width inside the card */}
              <Box sx={{ gridColumn: "1 / -1", pt: 0.75, borderTop: "1px solid #e2e8f0",
                display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Customer Notes</Typography>
                  <Typography variant="body2"
                    sx={{ color: cl.CustomerComment ? "text.primary" : "text.disabled",
                      whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                    {cl.CustomerComment || "No notes."}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => setNoteModalOpen(true)}
                  sx={{ textTransform: "none", fontSize: "0.72rem", borderColor: Z_PURPLE, color: Z_PURPLE,
                    py: 0.25, flexShrink: 0, mt: "18px" }}>
                  Update Note
                </Button>
              </Box>
            </Box>

            {/* Total Owing */}
            <Box sx={{
              border: `1px solid ${Z_PURPLE}22`,
              borderRadius: 2, p: 1.5,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              background: data.totalOwing > 0 ? "#fff5f5" : "#f0fdf4",
              gap: 0.5,
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} textAlign="center">
                Total Owing
              </Typography>
              <Typography fontWeight={900}
                sx={{ fontSize: "1.5rem", color: data.totalOwing > 0 ? "#dc2626" : "#16a34a", lineHeight: 1.2 }}>
                {fmt(data.totalOwing)}
              </Typography>
            </Box>

            {/* Invoiced To */}
            <Box sx={{
              border: `1px solid ${Z_PURPLE}22`,
              borderRadius: 2, p: 1.5,
              display: "flex", flexDirection: "column", gap: 0.75,
              background: "#fff",
            }}>
              {(() => {
                const billedToInsurer = !!cl.InsCompName;
                const name  = cl.InsCompName || cl.EmployerName || "—";
                const phone = billedToInsurer ? cl.InsCompPhone : cl.EmployerPhone;
                const email = billedToInsurer ? cl.InsCompEmail : cl.EmployerEmail;
                const fax   = billedToInsurer ? null : cl.EmployerFax;
                const notes = billedToInsurer ? cl.InsComments : cl.EmployerComments;
                return (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Invoiced To
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>{name}</Typography>
                      {phone && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {phone}
                        </Typography>
                      )}
                      {fax && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Fax {fax}
                        </Typography>
                      )}
                      {email && (
                        <Typography
                          component="a"
                          href={`mailto:${email}`}
                          variant="caption"
                          display="block"
                          sx={{ color: Z_BLUE, wordBreak: "break-all", textDecoration: "none",
                                "&:hover": { textDecoration: "underline" } }}
                        >
                          {email}
                        </Typography>
                      )}
                    </Box>
                    {notes && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {billedToInsurer ? "Insurer Notes" : "Employer Notes"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word" }}>
                          {notes}
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>

          <Divider />

          {/* ── Comments + Outstanding Invoices side by side ── */}
          <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>

            {/* Comments */}
            <Box sx={{ width: 360, flexShrink: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1, color: Z_PURPLE }}>Comments</Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add a comment…"
                  fullWidth
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && addComment()}
                />
                <Button
                  variant="contained"
                  startIcon={<AddCommentOutlinedIcon />}
                  onClick={addComment}
                  disabled={addingComment || !commentText.trim()}
                  sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0,
                    background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{
                maxHeight: 220, overflowY: "auto",
                display: "flex", flexDirection: "column", gap: 0.75,
                border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1, background: "#fafafa",
              }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 0.5 }}>No comments.</Typography>
                ) : (
                  comments.map((cm) => (
                    <Box key={cm.CommentID} sx={{ background: "#fff", borderRadius: 1, p: 1, border: "1px solid #e2e8f0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: Z_PURPLE }}>
                          {cm.StaffName || "Unknown"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{fmtDate(cm.CommentDate)}</Typography>
                          {editingCommentID !== cm.CommentID && (
                            <>
                              <Tooltip title="Edit comment">
                                <IconButton size="small" onClick={() => startEditComment(cm)} sx={{ p: 0.25 }}>
                                  <EditOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete comment">
                                <IconButton
                                  size="small"
                                  onClick={() => confirmDeleteComment(cm)}
                                  disabled={deletingCommentID === cm.CommentID}
                                  sx={{ p: 0.25 }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: "0.95rem" }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                      {editingCommentID === cm.CommentID ? (
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end" }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            autoFocus
                          />
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={saveEditComment}
                              disabled={savingComment || !editingCommentText.trim()}
                            >
                              <CheckIcon sx={{ fontSize: "1rem", color: Z_PURPLE }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton size="small" onClick={cancelEditComment} disabled={savingComment}>
                              <CloseIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{cm.Comment}</Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {/* Outstanding Invoices */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>Outstanding Invoices</Typography>
                {outstandingInvoices.length >= 2 && (
                  <Tooltip title="Merge selected invoices into one">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MergeTypeIcon fontSize="small" />}
                      onClick={() => setMergeModalOpen(true)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", borderColor: Z_PURPLE, color: Z_PURPLE,
                        py: 0.25, px: 1 }}
                    >
                      Merge Invoices
                    </Button>
                  </Tooltip>
                )}
                <ZephyrEmailButtons
                  claimID={claimID}
                  insurerEmail={cl?.InsCompEmail ?? ""}
                  onOpenCompose={() => setComposeModal({ claimID, customerName: cl?.FullName ?? "", claimNumber: cl?.ClaimNumber ?? "", chemistEmail: cl?.ChemistEmail ?? "" })}
                  onSuccess={(msg) => showToast(msg)}
                  onError={(msg) => showToast(msg, "error")}
                />
              </Box>
              {outstandingInvoices.length > 0 && (
                <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                      <TableRow>
                        {[
                          { label: "Invoice" },
                          { label: "Date" },
                          { label: "Scripts", sx: { width: "1px", whiteSpace: "nowrap" } },
                          { label: "Value", align: "right" },
                          { label: "Comment", sx: { width: "100%" } },
                          { label: "" },
                        ].map(({ label, align, sx: colSx }, i) => (
                          <TableCell key={i} align={align ?? "left"}
                            sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1, ...colSx }}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {outstandingInvoices.map((inv) => (
                        <TableRow key={inv.InvoiceID} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                          <TableCell>
                            <button onClick={() => setInvoiceDetailID(inv.InvoiceID)}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                                color: Z_PURPLE, fontWeight: 700, textDecoration: "underline", fontSize: "inherit" }}>
                              #{inv.InvoiceID}
                            </button>
                          </TableCell>
                          <TableCell>{fmtDate(inv.InvoiceDate)}</TableCell>
                          <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>{inv.ScriptCount ?? "—"}</TableCell>
                          <TableCell align="right">{fmt(inv.InvoiceValue)}</TableCell>
                          <TableCell>
                            <span style={{ color: inv.InvComment ? "inherit" : "#94a3b8" }}>
                              {inv.InvComment || "—"}
                            </span>
                            {inv.RecheckDate && (
                              <Typography variant="caption" display="block" sx={{ color: "#92400e", mt: 0.25 }}>
                                Recheck: {fmtDate(inv.RecheckDate)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ width: "1px", whiteSpace: "nowrap", px: "4px !important" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                              <Tooltip title="Edit invoice comment">
                                <IconButton size="small"
                                  onClick={() => setInvoiceCommentState({ invoiceID: inv.InvoiceID, currentComment: inv.InvComment || "" })}
                                  sx={{ color: Z_PURPLE, p: 0.5 }}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <ZephyrEmailButtons
                                invoiceID={inv.InvoiceID}
                                insurerEmail={cl?.InsCompEmail ?? ""}
                                onOpenCompose={() => setComposeModal({ invoiceID: inv.InvoiceID, claimID, customerName: cl?.FullName ?? "", claimNumber: cl?.ClaimNumber ?? "", chemistEmail: cl?.ChemistEmail ?? "" })}
                                onSuccess={(msg) => showToast(msg)}
                                onError={(msg) => showToast(msg, "error")}
                              />
                              <WriteOffButton
                                claimID={claimID}
                                invoiceID={inv.InvoiceID}
                                scripts={(data?.scripts ?? []).filter((s) => s.InvoiceID === inv.InvoiceID)}
                                onSuccess={() => loadCustomer(claimID)}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>

        </>
      )}

      {/* ── Sub-modals ── */}
      <EditClaimModal
        open={editClaimOpen}
        claim={cl}
        onClose={() => setEditClaimOpen(false)}
        onSaved={() => { showToast("Claim updated."); claimID && loadCustomer(claimID); }}
      />
      <CustomerNoteModal
        open={noteModalOpen}
        initialValue={cl?.CustomerComment ?? ""}
        customerID={cl?.ClaimID}
        customerName={cl?.FullName}
        onClose={() => setNoteModalOpen(false)}
        onSaved={(val) => setData((prev) => prev ? { ...prev, claim: { ...prev.claim, CustomerComment: val } } : prev)}
      />
      <InvoiceDetailModal
        open={!!invoiceDetailID}
        invoiceID={invoiceDetailID}
        claimID={claimID}
        onClose={() => setInvoiceDetailID(null)}
        onWriteOff={() => claimID && loadCustomer(claimID)}
      />
      <InvoiceCommentModal
        open={!!invoiceCommentState}
        invoiceID={invoiceCommentState?.invoiceID}
        claimID={claimID}
        currentComment={invoiceCommentState?.currentComment}
        onClose={() => setInvoiceCommentState(null)}
        onSaved={handleInvoiceCommentSaved}
      />
      <FullReportDrawer
        open={!!reportMode}
        claimID={claimID}
        customerName={cl?.FullName}
        outstandingOnly={reportMode === "outstanding"}
        onClose={() => setReportMode(null)}
        onOpenPayment={(id) => setPaymentModal(id)}
        onOpenRecon={(id) => setReconModal(id)}
      />
      <PaymentModal open={!!paymentModal} paymentID={paymentModal} onClose={() => setPaymentModal(null)} />
      <ReconModal open={!!reconModal} reconID={reconModal} onClose={() => setReconModal(null)} />
      <MergeInvoicesModal
        open={mergeModalOpen}
        outstandingInvoices={outstandingInvoices}
        claimID={claimID}
        onClose={() => setMergeModalOpen(false)}
        onSuccess={(msg) => { showToast(msg); setMergeModalOpen(false); claimID && loadCustomer(claimID); }}
        onError={(msg) => showToast(msg, "error")}
      />

      <ZephyrComposeModal
        open={!!composeModal}
        onClose={() => setComposeModal(null)}
        invoiceID={composeModal?.invoiceID ?? null}
        claimID={composeModal?.claimID ?? claimID}
        insurerEmail={cl?.InsCompEmail ?? ""}
        customerName={composeModal?.customerName ?? cl?.FullName ?? ""}
        claimNumber={composeModal?.claimNumber ?? cl?.ClaimNumber ?? ""}
        chemistEmail={composeModal?.chemistEmail ?? cl?.ChemistEmail ?? ""}
        onSuccess={(msg) => { showToast(msg); setComposeModal(null); }}
        onError={(msg) => showToast(msg, "error")}
      />

      <Dialog open={!!deleteConfirmComment} onClose={() => setDeleteConfirmComment(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete comment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove this comment from the claim history. This cannot be undone.
          </Typography>
          {deleteConfirmComment && (
            <Box sx={{ mt: 1.5, p: 1, background: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: Z_PURPLE }}>
                {deleteConfirmComment.StaffName || "Unknown"} · {fmtDate(deleteConfirmComment.CommentDate)}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.25 }}>{deleteConfirmComment.Comment}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmComment(null)} disabled={!!deletingCommentID}>Cancel</Button>
          <Button onClick={deleteComment} color="error" variant="contained" disabled={!!deletingCommentID}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.severity ?? "success"} onClose={hideToast} sx={{ width: "100%" }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
